import { Button } from "@/components/ui/button";
import WidgetCard from "./widget-card";
import { CircleArrowRight, Hospital } from "lucide-react";
import type Widget from "@/model/widget";
import ProviderSearchResultSkeleton from "../provider-search/provider-search-result-skeleton";
import useAuth from "@/context/auth-provider";
import { queryClient } from "@/hooks/use-query-client";
import {
  DISTANCE_OPTIONS,
  PROVIDER_SEARCH_RESULT_ITEM_REQUIRED_KEYS,
  PROVIDER_SEARCH_TYPES,
  QueryKeys,
} from "@/constants";
import type { Demographics } from "@/model/demographics";
import { useNearestProviderSearch } from "@/hooks/use-provider-search";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import type {
  IProviderSearchPayload,
  ProviderSearchResultItemType,
} from "@/types";
import ProviderDetails from "../provider-search/provider-details";
import { getGoogleMapsDirectionUrl } from "@/lib/map-utils";
import {
  setDistance,
  setLocation,
  setProviderSearchDynamicPayload,
  setProviderSearchType,
  setSearchCriteria,
  setSpecialty,
  setSpecialtyList,
  setTaxonomyCode,
} from "@/store/provider-search/provider-search-slice";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useNavigate } from "react-router-dom";
import { NoDataAvailable } from "@/pages/components/empty";
import { useUserLocation } from "@/context/location-provider";

type NearestProviderWidgetProps = {
  className?: string;
  widget: Widget;
};

const hasResultKeySet = (arr: unknown[]): boolean =>
  arr?.some(
    (obj) =>
      typeof obj === "object" &&
      obj !== null &&
      PROVIDER_SEARCH_RESULT_ITEM_REQUIRED_KEYS.every((key) => key in obj),
  );

const NearestProviderWidget: React.FunctionComponent<
  NearestProviderWidgetProps
> = ({ widget }) => {
  const { authData } = useAuth();
  const navigate = useNavigate();

  const { location, isFetching: isLocationFetching } = useUserLocation();
  const { network } = useSelector((state: RootState) => state.providerSearch);
  const demographicsData = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData?.user?.username,
  ]) as Demographics;
  const dispatch = useDispatch();
  const nearestProviderSearchPayload: IProviderSearchPayload = {
    memberId: demographicsData.memberId,
    divisionId: demographicsData.divisionId,
    groupId: demographicsData.groupId,
    planId: demographicsData?.planId,
    token: authData!.user!.access_token,
    distance: DISTANCE_OPTIONS[5].value,
    lat: location?.lat || 0,
    lon: location?.lng || 0,
    limit: 2,
    offset: 0,
    network,
    category: widget.providerCategory!,
    taxonomyCode: widget.taxonomyCode!,
    searchCriteria: widget.searchCriteria!,
  };

  const isFromWidget = true;
  const {
    data: nearestSearchResult,
    isFetching: isFetchingNearestProvider,
    isError: isNearestProviderError,
  } = useNearestProviderSearch(
    nearestProviderSearchPayload,
    widget?.widgetId,
    isFromWidget,
  );

  const getDirection = (providerLat: number, providerLng: number) => {
    const user = { lat: location!.lat, lng: location!.lng };
    const provider = { lat: providerLat, lng: providerLng };
    const url = getGoogleMapsDirectionUrl(user, provider, "driving");
    window.open(url, "_blank");
  };

  const callProvider = (tel: string) => {
    window.location.href = `tel:${tel}`;
  };

  const viewFullProvider = () => {
    dispatch(
      setSpecialty({
        searchParameter: "",
        specialtyDesc: "",
        type: "",
      }),
    );
    dispatch(
      setLocation({
        description: location!.description,
        place_id: location!.place_id,
        reference: location!.reference,
        lat: location!.lat,
        lng: location!.lng,
      }),
    );
    dispatch(setSpecialtyList([]));
    dispatch(setProviderSearchType(PROVIDER_SEARCH_TYPES.QUICK_SEARCH));
    dispatch(
      setProviderSearchDynamicPayload({
        category: widget.providerCategory,
        searchCriteria: widget?.searchCriteria,
        taxonomyCode: widget.taxonomyCode,
      }),
    );
    dispatch(setDistance(DISTANCE_OPTIONS[5]));
    dispatch(setSearchCriteria(widget.searchCriteria));
    dispatch(setTaxonomyCode(widget.taxonomyCode));
    const targetPath = `/${ScreenRouteNames.PROVIDER_SEARCH_BY_GROUP.path}/${ScreenRouteNames.PROVIDER_SEARCH_RESULT.path}`;
    navigate(targetPath, {
      replace: false,
    });
  };

  const renderWidget = () => {
    if (isFetchingNearestProvider || isLocationFetching) {
      return <ProviderSearchResultSkeleton />;
    } else if (isNearestProviderError) {
      return (
        <NoDataAvailable
          message="Sorry, we couldn't find any providers"
          icon={Hospital}
        />
      );
    } else {
      if (hasResultKeySet(nearestSearchResult?.results)) {
        return nearestSearchResult?.results?.map(
          (item: ProviderSearchResultItemType, index: number) => {
            return (
              <div key={`${widget?.widgetId}-${index}`} className="mb-3">
                <ProviderDetails
                  index={index}
                  providerDetail={item}
                  getDirection={getDirection}
                  callProvider={callProvider}
                />
              </div>
            );
          },
        );
      } else {
        return (
          <NoDataAvailable
            message="Sorry, we couldn't find any providers"
            icon={Hospital}
          />
        );
      }
    }
  };

  const footerContent = (
    <Button
      variant="link"
      className="w-full hover:underline"
      onClick={viewFullProvider}
    >
      View Full Provider <CircleArrowRight className="ml-1" />
    </Button>
  );

  return (
    <WidgetCard title={widget?.widgetDisplayName} footerContent={footerContent}>
      {renderWidget()}
    </WidgetCard>
  );
};

export default NearestProviderWidget;
